import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useChatContext } from 'stream-chat-react'
import type { Channel } from 'stream-chat'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import {
  getInitials,
  getInitialsFromName,
  getChannelName,
  getUserName,
} from 'components/Chat/helper'
import { isGroupChannel } from 'components/Chat/utils/channelHelpers'
import { formatMessageWithMentions } from 'components/Chat/helper/messageFormatter'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { getChipColorsFromText, getHexColorFromText } from 'appUtils/colorUtils'
import DOMPurify from 'dompurify'
import { formatFileSize } from 'utils/helper'
import { ReactComponent as FileIcon } from 'assets/svgs/file-icon-2.svg'

interface ForwardMessageModalProps {
  show: boolean
  message: any
  onCancel: () => void
  onConfirm: (selectedUsers: string[], selectedChannels: Channel[]) => void
}

// Helper function to sanitize HTML and ensure links open in new tab
const sanitizeAndProcessLinks = (html: string): string => {
  // First sanitize with DOMPurify
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['mention'],
    ADD_ATTR: ['id', 'target', 'rel', 'href'],
  })

  // Create a temporary DOM element to process links
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = sanitized

  // Find all anchor tags and add target="_blank" and rel="noopener noreferrer"
  const links = tempDiv.querySelectorAll('a')
  links.forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  return tempDiv.innerHTML
}

// Message Preview Component
const MessagePreview = ({ message }: { message: any }) => {
  const { t } = useTranslation()
  const hasText = message?.text && message.text.trim().length > 0
  const hasAttachments = message?.attachments && message.attachments.length > 0
  const hasTranscript = message?.custom?.transcript && message.custom.transcript.trim().length > 0
  const hasRichText = message?.custom?.richTextHtml || message?.html

  // Filter attachments
  const imageAttachments = hasAttachments
    ? message.attachments.filter((att: any) => att.type === 'image' && !!att.image_url)
    : []
  const fileAttachments = hasAttachments
    ? message.attachments.filter((att: any) => {
        if (att.type !== 'file') return false
        // Exclude voice messages
        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) return false
        if (att.mime_type?.startsWith('audio/')) return false
        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        )
          return false
        return true
      })
    : []

  // If no content at all, don't show preview
  if (!hasText && !hasAttachments && !hasTranscript && !hasRichText) {
    return null
  }

  return (
    <div className="bg-app-primary-60 rounded-2xl px-4 py-3 max-w-sm text-app-white-100">
      {/* Render rich text HTML or plain text */}
      {(hasRichText || hasText) && (
        <div className="text-sm leading-relaxed break-words mb-2 [&_*]:text-inherit">
          {message?.custom?.richTextHtml ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-white-100 [&_a:hover]:text-app-white-100 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-white-100/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-white-100 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.custom.richTextHtml),
              }}
            />
          ) : message?.html ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-white-100 [&_a:hover]:text-app-white-100 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-white-100/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-white-100 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.html),
              }}
            />
          ) : (
            <div>{formatMessageWithMentions(message.text)}</div>
          )}
        </div>
      )}

      {/* Render transcript if available */}
      {hasTranscript && (
        <div className="text-xs text-app-white-100/80 italic mb-2">
          <span className="text-app-white-100/60 mr-1">{t('chat.forward.transcript')}</span>
          {message.custom.transcript}
        </div>
      )}

      {/* Render image attachments */}
      {imageAttachments.length > 0 && (
        <div className="mb-2">
          <div className="grid grid-cols-2 gap-2">
            {imageAttachments.slice(0, 4).map((attachment: any, idx: number) => (
              <div
                key={`img-${idx}`}
                className="relative w-full aspect-square rounded-lg overflow-hidden"
              >
                <img
                  src={attachment.image_url}
                  alt={attachment.fallback || attachment.title || 'Image'}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          {imageAttachments.length > 4 && (
            <div className="text-xs text-app-white-100/80 mt-1">
              {t('chat.forward.moreImages', { count: imageAttachments.length - 4 })}
            </div>
          )}
        </div>
      )}

      {/* Render file attachments */}
      {fileAttachments.length > 0 && (
        <div className="space-y-1.5">
          {fileAttachments.slice(0, 3).map((attachment: any, idx: number) => {
            const fileName = attachment.title || attachment.file_name || attachment.name || 'File'
            const fileSizeText = attachment.file_size ? formatFileSize(attachment.file_size) : ''
            return (
              <div
                key={`file-${idx}`}
                className="flex items-center gap-2 bg-app-white-100/10 rounded-lg p-2"
              >
                <div className="w-6 h-6 rounded bg-app-white-100/20 flex items-center justify-center flex-shrink-0">
                  <FileIcon className="w-4 h-4 text-app-white-100" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-app-white-100 truncate">{fileName}</div>
                  {fileSizeText && (
                    <div className="text-[10px] text-app-white-100/70">{fileSizeText}</div>
                  )}
                </div>
              </div>
            )
          })}
          {fileAttachments.length > 3 && (
            <div className="text-xs text-app-white-100/80">
              {t('chat.forward.moreFiles', { count: fileAttachments.length - 3 })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const ForwardMessageModal: React.FC<ForwardMessageModalProps> = ({
  show,
  message,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [selectedChannels, setSelectedChannels] = useState<Channel[]>([])
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [recentChats, setRecentChats] = useState<Channel[]>([])
  const [activeTab, setActiveTab] = useState<'recent' | 'contacts'>('recent')

  const { client } = useChatContext()
  const { userName } = useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Memoize assignee options
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (!isAdvisor) {
      options = businessAssigneeOptions || []
    } else {
      const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      options =
        response?.accounts?.map((account: any) => ({
          label: `${account?.firstName} ${account?.lastName}`,
          value: account?.id,
          user: {
            id: account?.id,
            firstName: account?.firstName,
            lastName: account?.lastName,
          },
        })) || []
    }

    // Filter out current user
    return options.filter((option) => option.value !== loggedInUser?.id)
  }, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

  useEffect(() => {
    if (show) {
      setUsers(assigneeOptions)

      // Get recent chats
      if (client) {
        const channels = Object.values(client.activeChannels).sort((a: any, b: any) => {
          // Sort by last message time
          const aTime = a.state.last_message_at || a.state.created_at
          const bTime = b.state.last_message_at || b.state.created_at
          return new Date(bTime).getTime() - new Date(aTime).getTime()
        })

        setRecentChats(channels as Channel[])
      }
    }
  }, [show, assigneeOptions, client])

  const getChannelDisplayName = (channel: any): string => {
    if (channel.data?.name) {
      return channel.data.name
    }
    const otherMembers = Object.values(channel.state.members || {}).filter(
      (member: any) => member?.user?.id !== client.userID
    )
    if (otherMembers.length === 1) {
      return getUserName((otherMembers[0] as any)?.user) || t('chat.forward.unknownUser')
    }
    return getChannelName(otherMembers) || t('chat.forward.unknownChat')
  }

  const filteredUsers = users.filter((user) =>
    user.label.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredChats = recentChats.filter((channel: any) => {
    const displayName = getChannelDisplayName(channel)
    return displayName.toLowerCase().includes(searchQuery.toLowerCase())
  })

  const handleUserToggle = (userId: string) => {
    const newSelected = new Set(selectedUsers)
    if (newSelected.has(userId)) {
      newSelected.delete(userId)
    } else {
      newSelected.add(userId)
    }
    setSelectedUsers(newSelected)
  }

  const handleChannelSelect = (channel: Channel) => {
    setSelectedChannels((prev) =>
      prev.find((ch) => ch.cid === channel.cid)
        ? prev.filter((ch) => ch.cid !== channel.cid)
        : [...prev, channel]
    )
  }

  const handleForward = () => {
    onConfirm(Array.from(selectedUsers), selectedChannels)
    handleClose()
  }

  const handleClose = () => {
    setSearchQuery('')
    setSelectedUsers(new Set())
    setSelectedChannels([])
    setActiveTab('recent')
    onCancel()
  }

  if (!show) return null

  const hasSelections = selectedUsers.size > 0 || selectedChannels.length > 0

  return (
    <AppModalBox
      width="w-[500px]"
      backgroundColor="bg-white"
      childClass="p-0"
      showCancelButton={false}
      margin=""
      height="h-auto"
      isOnBoarding={false}
      padding="p-0"
      position="center"
      rounded="xl"
      showBorder={false}
    >
      <div className="flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-app-grey-30">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-app-black-100">{t('chat.forward.title')}</h3>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-app-grey-10 rounded-lg transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Search with Chips */}
        <div className="px-6 py-4">
          <div className="relative">
            <div className="flex items-center gap-1 flex-wrap min-h-[42px] px-3 py-1.5 bg-app-white-100 border border-app-grey-30 rounded-lg focus-within:ring-2 focus-within:ring-app-primary-60 focus-within:border-transparent">
              {/* Selected Users Chips */}
              {Array.from(selectedUsers).map((userId) => {
                const user = users.find((u) => u.value === userId)
                if (!user) return null
                const userName = `${user.user?.firstName || ''} ${user.user?.lastName || ''}`.trim()
                const userInitials = getInitials({
                  firstName: user.user?.firstName || '',
                  lastName: user.user?.lastName || '',
                })
                const chipColors = getChipColorsFromText(userName)
                const avatarColor = getHexColorFromText(userName)

                return (
                  <div
                    key={`user-${userId}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold"
                    style={{
                      backgroundColor: chipColors.backgroundColor,
                      color: chipColors.textColor,
                      borderRadius: '8px',
                      paddingTop: '6px',
                      paddingRight: '12px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                    }}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{
                        backgroundColor: avatarColor.color,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                        textShadow: avatarColor.isLight ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.2)',
                        boxSizing: 'border-box',
                      }}
                    >
                      {userInitials}
                    </div>
                    <span className="font-semibold" style={{ color: chipColors.textColor }}>
                      {userName}
                    </span>
                    <button
                      onClick={() => handleUserToggle(userId)}
                      className="w-4 h-4 opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: chipColors.textColor }}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )
              })}

              {/* Selected Channels Chips */}
              {selectedChannels.map((channel) => {
                const displayName = getChannelDisplayName(channel)
                const isGroup = isGroupChannel(channel)
                const otherMembers = Object.values(channel.state.members || {}).filter(
                  (member: any) => member?.user?.id !== client.userID
                ) as any[]
                const chipColors = getChipColorsFromText(displayName)
                const avatarColor = getHexColorFromText(displayName)

                return (
                  <div
                    key={`channel-${channel.cid}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold"
                    style={{
                      backgroundColor: chipColors.backgroundColor,
                      color: chipColors.textColor,
                      borderRadius: '8px',
                      paddingTop: '6px',
                      paddingRight: '12px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                    }}
                  >
                    {isGroup ? (
                      <div className="w-7 h-7 rounded-full bg-app-grey-30 flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-4 h-4 text-app-grey-70"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{
                          backgroundColor: avatarColor.color,
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                          textShadow: avatarColor.isLight
                            ? 'none'
                            : '0px 1px 2px rgba(0, 0, 0, 0.2)',
                          boxSizing: 'border-box',
                        }}
                      >
                        {getInitials({
                          firstName: otherMembers[0]?.user?.firstName || '',
                          lastName: otherMembers[0]?.user?.lastName || '',
                        })}
                      </div>
                    )}
                    <span className="font-semibold" style={{ color: chipColors.textColor }}>
                      {displayName}
                    </span>
                    <button
                      onClick={() => handleChannelSelect(channel)}
                      className="w-4 h-4 opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: chipColors.textColor }}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )
              })}

              <input
                type="text"
                placeholder={t('chat.forward.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm placeholder-app-grey-55 px-1"
              />

              <svg
                className="w-4 h-4 text-app-grey-55 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-app-grey-30">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('recent')}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 ${
                activeTab === 'recent'
                  ? 'border-app-primary-60 text-app-primary-60'
                  : 'border-transparent text-app-grey-60 hover:text-app-grey-90'
              }`}
            >
              {t('chat.forward.tabs.recent')}
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`pb-3 border-b-2 font-medium text-sm transition-colors duration-150 ${
                activeTab === 'contacts'
                  ? 'border-app-primary-60 text-app-primary-60'
                  : 'border-transparent text-app-grey-60 hover:text-app-grey-90'
              }`}
            >
              {t('chat.forward.tabs.all')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-64">
          <div className="px-6">
            {activeTab === 'recent' ? (
              // Recent Chats Tab
              filteredChats.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-app-grey-60">{t('chat.forward.emptyRecent')}</p>
                </div>
              ) : (
                filteredChats.map((channel) => {
                  const isSelected = selectedChannels.find((ch) => ch.cid === channel.cid)
                  const displayName = getChannelDisplayName(channel)
                  const isGroup = isGroupChannel(channel)
                  const otherMembers = Object.values(channel.state.members || {}).filter(
                    (member: any) => member?.user?.id !== client.userID
                  ) as any[]

                  return (
                    <div
                      key={channel.cid}
                      className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
                      onClick={() => handleChannelSelect(channel)}
                    >
                      {isGroup ? (
                        <div className="w-10 h-10 bg-app-grey-30 rounded-full flex items-center justify-center flex-shrink-0">
                          {(channel as any).data?.image ? (
                            <img
                              src={(channel as any).data.image}
                              alt="Group icon"
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <svg
                              className="w-5 h-5 text-app-grey-70"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                          )}
                        </div>
                      ) : (
                        <AppAvatar
                          size="md"
                          type="mixed"
                          shape="Rounded"
                          text={getInitials({
                            firstName: otherMembers[0]?.user?.firstName || '',
                            lastName: otherMembers[0]?.user?.lastName || '',
                          })}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-app-black-100 truncate">
                          {displayName}
                        </p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-app-primary-60 border-app-primary-60'
                            : 'border-app-grey-40 bg-app-white-100 hover:border-app-primary-60'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 text-app-white-100"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  )
                })
              )
            ) : // All Contacts Tab
            filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-app-grey-60">
                  {searchQuery
                    ? t('chat.forward.emptyContactsQuery', { query: searchQuery })
                    : t('chat.forward.emptyContacts')}
                </p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.value}
                  className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
                  onClick={() => handleUserToggle(user.value)}
                >
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: user.user?.firstName || '',
                      lastName: user.user?.lastName || '',
                    })}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-black-100 truncate">{user.label}</p>
                  </div>
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedUsers.has(user.value)
                        ? 'bg-app-primary-60 border-app-primary-60'
                        : 'border-app-grey-40 bg-app-white-100 hover:border-app-primary-60'
                    }`}
                  >
                    {selectedUsers.has(user.value) && (
                      <svg
                        className="w-3 h-3 text-app-white-100"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Preview */}
        {message && (
          <div className="px-6 py-4 border-t border-app-grey-30 bg-app-grey-5">
            <div className="flex items-end justify-end gap-3">
              <MessagePreview message={message} />
              <AppAvatar
                size="md"
                type="mixed"
                shape="Rounded"
                text={getInitialsFromName(userName)}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-app-grey-30">
          <div className="flex items-center justify-end">
            {/* <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-app-grey-60 hover:text-app-black-100 transition-colors"
            >
              Copy link
            </button> */}
            <div className="flex gap-3">
              <AppButton
                size="md"
                variant={hasSelections ? 'primary' : 'secondary'}
                label={t('chat.forward.confirm')}
                onClick={handleForward}
                disabled={!hasSelections}
              />
            </div>
          </div>
        </div>
      </div>
    </AppModalBox>
  )
}
